import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { setNewChatModel } from "./ChatModelSlice";
import { conversationsActions } from "./conversationSlice";
import { ChatModelState, DefaultModelMode, PersonaItemData } from "../../Helper";
import { SelectedPersona } from "../../Interfaces";
interface ChatModelData {
  id: string;
  version: string;
  name:string;
}
interface FileErrorMsgState {
  errorMsg: string;
  isError: boolean;
  reason: string;
}
interface SnackBarAlert {
  message: string;
  show: boolean;
  messageBody?:string,
  alertType?: "error" | "warning" | "info" | "success";
  position?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
}

interface SignInPopup {
  show: boolean,
  type: string,
  message?: string
  btnTitle:string,
  btnAction?:string;
}
interface LimitAlert {
  show: boolean,
  message:string,
  btnTitle:string,
  btnAction?:string
}

interface ModeAccessibility {
  mode:number;
  modeAccessibility:number;
}

interface FreeTrialMessage {
  show: boolean,
  title: string,
  description: string,
  btnTitle: string,
  isError: boolean,
}


interface ChatsState {
  chatsNum: number;
  maximizedChat: {
    versionId: string;
    versionName: string;
  } | null;
  selectedOptions: string[];
  fullWidthMode: string;
  isUserInteracted: boolean[];
  reGenerateResponse: boolean;
  newConversation: boolean;
  Chat1Data: ChatModelData;
  Chat2Data: ChatModelData;
  Chat3Data: ChatModelData;
  Chat4Data: ChatModelData;
  Chat5Data: ChatModelData;
  Chat6Data: ChatModelData;
  GPTVersionID: string | null;
  GeminiVersionID: string;
  LlamaVersionID: string;
  isUpLoadedFile: boolean;
  selectedPersona: PersonaItemData;
  popupSaveConShown: boolean;
  disabledVersions: string[];
  messageError: string;
  reGenerateMessage: string;
  fileErrorMsg: FileErrorMsgState;
  ResponsiveAppSize: string;
  showNavMenu: boolean;
  ParentMessageId: string | null;
  IsUserType: boolean;
  currentObj?: ChatModelState;
  isEditing:boolean
  snackAlert: SnackBarAlert;
  currentSelectedPersonaFilter?: SelectedPersona;
  minimizeSidebar:boolean;
  showSignPopup: SignInPopup;
  defaultModelMode: DefaultModelMode[];
  limitAlert: LimitAlert;
  modeAccessibility:ModeAccessibility[];
  showPlansModal: boolean;
  displayConAtBottom: boolean;
  freeTrialMessage?: FreeTrialMessage;
  isGenerateImage: boolean;
  isGlobalSearch:boolean;
  canSetActiveModels: boolean;
  modelResponseLanguage: string;
}

const initialState: ChatsState = {
  chatsNum: 0,
  maximizedChat: null,
  selectedOptions: [],
  fullWidthMode: "",
  isUserInteracted: [false, false, false, false, false, false],
  reGenerateResponse: false,
  newConversation: false,
  GPTVersionID: "",
  Chat1Data: { id: "", version: "" ,name:""},
  Chat2Data: { id: "", version: "" ,name:""},
  Chat3Data: { id: "", version: "" ,name:""},
  Chat4Data: { id: "", version: "" ,name:""},
  Chat5Data: { id: "", version: "" ,name:""},
  Chat6Data: { id: "", version: "" ,name:""},
  GeminiVersionID: "",
  LlamaVersionID: "",
  isUpLoadedFile: false,
  popupSaveConShown: false,
  selectedPersona: { id: "", title: "", description: "", objective: "" },
  disabledVersions: [],
  messageError: "",
  reGenerateMessage: "",
  fileErrorMsg: { isError: false, errorMsg: "", reason: "" },
  showNavMenu: false,
  ResponsiveAppSize: "Desktop",
  ParentMessageId: null,
  IsUserType: false,
  isEditing:false,
  snackAlert: { show: false, message: "", alertType: "success",messageBody:"", position: {
    vertical:"bottom",
    horizontal: "right"
  } },
  currentSelectedPersonaFilter:{
    id:  null , 
    title:null,
    index: null,
    filterFrom: null,
    filterTo: null
  },
  minimizeSidebar:true,
  showSignPopup: {show: false , type: "" , message: "",btnTitle:''},
  defaultModelMode: [],
  limitAlert:{show:false,message:'',btnTitle:'',btnAction:""},
  modeAccessibility:[],
  showPlansModal:false,
  displayConAtBottom: true,
  freeTrialMessage: { show: false, title: '', description: '', btnTitle: '', isError: false },
  isGenerateImage: false,
  isGlobalSearch:false,
  canSetActiveModels: false,
  modelResponseLanguage: "English",
};
interface UserInteractionPayload {
  index: number;
  value: boolean;
}

const chatsSlice = createSlice({
  name: "chats",
  initialState: initialState,
  reducers: {
    getChatsOpen: (
      state,
      action: PayloadAction<{ chatsNum: number; selectedOptions: string[] }>
    ) => {
      state.chatsNum = action.payload.chatsNum;
      state.selectedOptions = action.payload.selectedOptions;
    },
    setMaximizedChat: (
      state,
      action: PayloadAction<{ versionId: string; versionName: string } | null>
    ) => {
      state.maximizedChat = action.payload;
    },
    setCurrentObjSave: (state, action: PayloadAction<ChatModelState>) => {
      state.currentObj = action.payload;
    },
    setIsEditingStatus: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    toggleFullWidthMode: (state, action: PayloadAction<string>) => {
      const model = action.payload;
      state.fullWidthMode = state.fullWidthMode === model ? "" : model;
    },
    setUserInteracted: (
      state,
      action: PayloadAction<UserInteractionPayload>
    ) => {
      const { index, value } = action.payload;
      state.isUserInteracted[index] = value;
    },
    resetUserInteracted: (state) => {
      state.isUserInteracted = state.isUserInteracted.map(() => false);
    },
    regenerateResponse: (state, action: PayloadAction<boolean>) => {
      state.reGenerateResponse = action.payload;
    },
    setNewConversation: (state, action: PayloadAction<boolean>) => {
      state.newConversation = action.payload;
    },
    setNewGPTVersion: (state, action: PayloadAction<string>) => {
      state.GPTVersionID = action.payload;
    },
    setNewGeminiVersion: (state, action: PayloadAction<string>) => {
      state.GeminiVersionID = action.payload;
    },
    setNewLlamaVersion: (state, action: PayloadAction<string>) => {
      state.LlamaVersionID = action.payload;
    },

    setChat1Data: (
      state,
      action: PayloadAction<{ id: string; version: string; name:string}>
    ) => {
      state.Chat1Data.id = action.payload.id;
      state.Chat1Data.version = action.payload.version;
      state.Chat1Data.name = action.payload.name;
    },
    setChat2Data: (
      state,
      action: PayloadAction<{ id: string; version: string; name:string}>
    ) => {
      state.Chat2Data.id = action.payload.id;
      state.Chat2Data.version = action.payload.version;
      state.Chat2Data.name = action.payload.name;
    },
    setChat3Data: (
      state,
      action: PayloadAction<{ id: string; version: string; name:string}>
    ) => {
      state.Chat3Data.id = action.payload.id;
      state.Chat3Data.version = action.payload.version;
      state.Chat3Data.name = action.payload.name;
    },
    setChat4Data: (
      state,
      action: PayloadAction<{ id: string; version: string; name:string}>
    ) => {
      state.Chat4Data.id = action.payload.id;
      state.Chat4Data.version = action.payload.version;
      state.Chat4Data.name = action.payload.name;
    },
    setChat5Data: (
      state,
      action: PayloadAction<{ id: string; version: string; name:string}>
    ) => {
      state.Chat5Data.id = action.payload.id;
      state.Chat5Data.version = action.payload.version;
      state.Chat5Data.name = action.payload.name;
    },
    setChat6Data: (
      state,
      action: PayloadAction<{ id: string; version: string; name:string}>
    ) => {
      state.Chat6Data.id = action.payload.id;
      state.Chat6Data.version = action.payload.version;
      state.Chat6Data.name = action.payload.name;
    },
    setUpLoadedFile: (state, action: PayloadAction<boolean>) => {
      state.isUpLoadedFile = action.payload;
    },
    setFileError: (state, action: PayloadAction<FileErrorMsgState>) => {
      state.fileErrorMsg.errorMsg = action.payload.errorMsg;
      state.fileErrorMsg.isError = action.payload.isError;
      state.fileErrorMsg.reason = action.payload.reason;
    },
    setSnackBarAlert: (state, action: PayloadAction<SnackBarAlert>) => {
      state.snackAlert.show = action.payload.show;
      state.snackAlert.message = action.payload.message;
      state.snackAlert.alertType = action.payload.alertType;
      state.snackAlert.messageBody = action.payload.messageBody;
      state.snackAlert.position = action.payload.position;
    },
    setSelectedPersona: (state, action: PayloadAction<any>) => {
      state.selectedPersona = action.payload;
    },
    setPopupConShown: (state, action: PayloadAction<boolean>) => {
      state.popupSaveConShown = action.payload;
    },
    setDisabledVersions: (state, action: PayloadAction<string[]>) => {
      state.disabledVersions = action.payload;
    },
    setMessageErrorLogin: (state, action: PayloadAction<string>) => {
      state.messageError = action.payload;
    },
    setMessageError: (state, action: PayloadAction<string>) => {
      state.messageError = action.payload;
    },
    setReGenerateMessage: (state, action: PayloadAction<string>) => {
      state.reGenerateMessage = action.payload;
    },
    setResponsiveAppSize: (state, action: PayloadAction<string>) => {
      state.ResponsiveAppSize = action.payload;
    },
    setShowNavMenu: (state, action: PayloadAction<boolean>) => {
      state.showNavMenu = action.payload;
    },
    setParentMessageId: (state, action: PayloadAction<string | null>) => {
      state.ParentMessageId = action.payload;
    },
    setIsUserType: (state, action: PayloadAction<boolean>) => {
      state.IsUserType = action.payload;
    },
    setCurrentPersonaFilter: (state, action: PayloadAction<{id : string | null , 
      title: string | null , index: number | null , filterFrom: string | null | undefined  , filterTo: string | null | undefined}>) => {
      if(state.currentSelectedPersonaFilter) {
        state.currentSelectedPersonaFilter.id = action.payload.id
        state.currentSelectedPersonaFilter.title = action.payload.title
        state.currentSelectedPersonaFilter.index = action.payload.index
        state.currentSelectedPersonaFilter.filterFrom = action.payload.filterFrom
        state.currentSelectedPersonaFilter.filterTo = action.payload.filterTo
      }
    },
    setMinimizeSidebar: (state, action: PayloadAction<boolean>) => {
      state.minimizeSidebar = action.payload;
    },
    setShowSignPopup: (state, action: PayloadAction<SignInPopup>) => {
      state.showSignPopup.show = action.payload.show;
      state.showSignPopup.type = action.payload.type;
      state.showSignPopup.message = action.payload.message;
      state.showSignPopup.btnTitle = action.payload.btnTitle;
      state.showSignPopup.btnAction = action.payload.btnAction;
    },
    setDefaultModelMode: (state , action:PayloadAction<DefaultModelMode[]>) => {
      state.defaultModelMode = action.payload;
       
      // Update modeAccessibility
      action.payload.forEach((modeData) => {
        const { mode, modeAccessibility } = modeData;
        state.modeAccessibility.push({ mode, modeAccessibility });
      });
    },
    setLimitAlert: (state , action:PayloadAction<LimitAlert>) => {
      state.limitAlert.show = action.payload.show;
      state.limitAlert.message = action.payload.message;
      state.limitAlert.btnTitle = action.payload.btnTitle;
      if(action.payload.btnAction)
      state.limitAlert.btnAction = action.payload.btnAction;
    },
    setShowPlansModal:(state, action:PayloadAction<boolean>) => {
      state.showPlansModal = action.payload;
    },
    setDisplayConAtBottom:(state, action:PayloadAction<boolean>) => {
      state.displayConAtBottom = action.payload;
    },
    setUserFreeTrialMessage: (state, action: PayloadAction<FreeTrialMessage>) => {
      state.freeTrialMessage = action.payload;
    },
    setIsGenerateImage: (state, action: PayloadAction<boolean>) => {
      state.isGenerateImage = action.payload;
    },
    setIsGlobalSearch: (state, action: PayloadAction<boolean>) => {
      state.isGlobalSearch = action.payload;
    },
    setCanSetActiveModels: (state, action: PayloadAction<boolean>) => {
      state.canSetActiveModels = action.payload;
    },
    setModelOutputLanguage: (state, action: PayloadAction<string>) => {
      state.modelResponseLanguage = action.payload;
    },
  },
});

export const handleNewConversationRequest = () => (dispatch: any) => {
    dispatch(setSelectedPersona(""));
    dispatch(setNewConversation(true));
    dispatch(conversationsActions.getLastActiveModels([]));
    dispatch(conversationsActions.removeActiveConversation());
    dispatch(setDisabledVersions([]));
    dispatch(setNewChatModel());
    dispatch(setLimitAlert({show:false,message:'',btnTitle:''}));
  };

export const {
  getChatsOpen,
  setMaximizedChat,
  toggleFullWidthMode,
  setUserInteracted,
  resetUserInteracted,
  regenerateResponse,
  setNewConversation,
  setNewGPTVersion,
  setNewGeminiVersion,
  setNewLlamaVersion,
  setUpLoadedFile,
  setIsEditingStatus,
  setCurrentObjSave,
  setSelectedPersona,
  setPopupConShown,
  setMessageError,
  setChat1Data,
  setChat2Data,
  setChat3Data,
  setChat4Data,
  setChat5Data,
  setChat6Data,
  setDisabledVersions,
  setMessageErrorLogin,
  setReGenerateMessage,
  setFileError,
  setShowNavMenu,
  setParentMessageId,
  setIsUserType,
  setSnackBarAlert,
  setCurrentPersonaFilter,
  setMinimizeSidebar,
  setShowSignPopup,
  setDefaultModelMode,
  setLimitAlert,
  setShowPlansModal,
  setDisplayConAtBottom,
  setIsGenerateImage,
  setIsGlobalSearch,
  setCanSetActiveModels,
  setModelOutputLanguage
} = chatsSlice.actions;
export default chatsSlice.reducer;